import { forwardRef, useCallback, useMemo } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { Pagination, PaginationProps } from '../Pagination/index.js';

import { useDataTableContext } from './hooks/index.js';
import {
  DataTablePaginationSelector,
  DataTablePaginationSelectorProps,
} from './DataTablePaginationSelector.js';

export type DataTableFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * If `true`, compact padding will be used.
   */
  dense?: boolean;
  /**
   * Callback fired when the page is changed.
   */
  onPaginate?: (event: any, meta: { newPage: number; rowsPerPage: number }) => void;
  /**
   * Callback fired when the page is changed.
   */
  onPageChange?: (event: any, newPage: number) => void;
  /**
   * Callback fired when the number of rows per page is changed.
   */
  onRowsPerPageChange?: (event: any, rowsPerPage: number) => void;
  /**
   * The options for the rows per page selector.
   */
  rowsPerPageOptions?: number[];
  /**
   * The component used to render the pagination.
   */
  paginationComponent?: React.ComponentType<PaginationProps>;
  /**
   * The component used to render the rows per page selector.
   */
  selectorComponent?: React.ComponentType<DataTablePaginationSelectorProps>;
  /**
   * The number of rows per page.
   */
  rowsPerPage?: number;
  /**
   * The total number of rows.
   */
  totalRows?: number;
  /**
   * The current page.
   */
  page?: number;
};

/**
 * The data table footer component.
 * @example
 * ```tsx
 * const YourWrapper = () => {
 *
 *   const [page, setPage] = useState(1);
 *   const [rowsPerPage, setRowsPerPage] = useState(10);
 *
 *   const handlePaginate = useCallback((evt, { newPage }) => {
 *    setPage(newPage);
 *   }, []);
 *
 *   const handleRowsPerPageChange = useCallback((evt, rowsPerPage) => {
 *     setRowsPerPage(rowsPerPage);
 *   }, []);
 *
 *
 *   const dataTableProps = useDataTable(
 *     // ...your data table options
 *   );
 *   <DataTable {...dataTableProps}>
 *     // ...rest of your table components
 *     <DataTableFooter
 *       rowsPerPage={rowsPerPage}
 *       totalRows={totalRows}
 *       page={page}
 *       onPaginate={handlePaginate}
 *       onRowsPerPageChange={handleRowsPerPageChange}
 *      />
 *    <DataTable>
 * }
 * ```
 */
export const DataTableFooter = styled(
  forwardRef(
    (
      {
        onPaginate,
        onPageChange,
        onRowsPerPageChange,
        rowsPerPageOptions = [10, 25, 50, 100],
        rowsPerPage = rowsPerPageOptions[0],
        totalRows: totalRowsProp,
        paginationComponent: PaginationComponent = Pagination,
        selectorComponent: SelectorComponent = DataTablePaginationSelector,
        page = 1,
        ...restProps
      }: DataTableFooterProps,
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { rows } = useDataTableContext();

      const totalRows = totalRowsProp ?? rows.length ?? 0;

      const handleChangePage = useCallback<PickProp<PaginationProps, 'onChange'>>(
        (evt, newPage) => {
          if (onPageChange) {
            onPageChange(evt, newPage);
          }

          if (onPaginate) {
            onPaginate(evt, {
              newPage: newPage,
              rowsPerPage: rowsPerPage,
            });
          }
        },
        [rowsPerPage, onPageChange, onPaginate]
      );

      const handleChangeRowsPerPage = useCallback<React.ChangeEventHandler>(
        (evt) => {
          const newRowsPerPage = (evt.target as any).value
            ? parseInt((evt.target as any).value as any, 10)
            : undefined;

          if (typeof newRowsPerPage === 'number' && !Number.isNaN(newRowsPerPage)) {
            if (onRowsPerPageChange) {
              onRowsPerPageChange(evt, newRowsPerPage);
            }
            if (onPaginate) {
              onPaginate(evt, {
                newPage: 1,
                rowsPerPage: newRowsPerPage,
              });
            }
          }
        },
        [onRowsPerPageChange, onPaginate]
      );

      const rowsPerPageItems = useMemo(
        () =>
          rowsPerPageOptions.map((value) => ({
            label: `${value}`,
            value,
          })),
        [rowsPerPageOptions]
      );

      const totalPages = Math.ceil(totalRows / rowsPerPage);

      return (
        <div ref={ref} {...restProps}>
          <SelectorComponent
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rows={rows.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageItems={rowsPerPageItems}
            totalRows={totalRows}
          />
          <PaginationComponent
            count={totalPages}
            disabled={totalPages <= 1}
            page={page}
            onChange={handleChangePage}
          />
        </div>
      );
    }
  )
).withConfig({
  displayName: 'DataTableFooter',
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top: 1px solid #e0e0e0;
  padding: 16px;
  gap: 16px;
  justify-content: space-between;

  & > div {
    display: flex;
    flex-flow: row;
    align-items: center;
    gap: 16px;
  }
`;
